export const isEmpty = (obj: any): boolean =>
  [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;

export const noop = (): undefined => {};

// Caution: This function does not handle all edge cases (e.g., NaN) - TMA (Jan 2026)
// Consider node:assert (http://nodejs.org/api/assert.html) .deepStrictEqual() for more complex applications.
//  - 'resolve.fallback: { assert: require.resolve("assert") }' in webpack.common.config.js
//  - 'yarn add assert'
export const isEqual = (x: any, y: any): boolean => {
  if (x && y && x.constructor !== y.constructor) return false;

  if (x instanceof Date) {
    return y instanceof Date && x.valueOf() === y.valueOf();
  }

  if (x && y && typeof x === 'object' && typeof y === 'object') {
    return (
      Object.keys(x).length === Object.keys(y).length &&
      Object.keys(x).every(key => isEqual(x[key], y[key]))
    );
  }

  return x === y;
};

export const range = (props: number[]): number[] => {
  const [first, second, third] = props;

  const start = second === undefined ? 0 : first;
  const end = second === undefined ? first : second;
  const step = third || 1;

  const length = third ? end / third : Math.abs(end - start);
  const factor = end < start ? -1 : 1;

  return [...Array(length).keys()].map(k => (k * factor + start) * step);
};

export const times = (n: number, iteratee?: (i: number) => any): any[] => {
  if (n < 0) return [];

  if (iteratee) {
    let i = -1;
    const result = Array(n);
    while (++i < n) {
      result[i] = iteratee(i);
    }
    return result;
  } else {
    return [...Array(n).keys()];
  }
};

export const shuffle = (arr: any[]): typeof arr => {
  let i = arr.length;
  while (--i > 0) {
    const random = Math.floor(Math.random() * i);
    [arr[i], arr[random]] = [arr[random], arr[i]];
  }
  return arr;
};

export const sortByKey = (arr: any[], key: string): any[] =>
  [...arr].sort((a, b): number => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0));

interface DebouncedFunc<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel(): void;
  flush(): ReturnType<T> | undefined;
}
interface DebounceOptions {
  leading?: boolean;
  maxWait?: number;
  trailing?: boolean;
}
// https://github.com/lodash/lodash/blob/f299b52f39486275a9e6483b60a410e06520c538/lodash.js#L10372-#L10495
export const debounce = (
  func: (...args: any[]) => any,
  wait: number,
  options?: DebounceOptions
): DebouncedFunc<typeof func> => {
  let lastArgs: Parameters<typeof func> | undefined,
    lastThis: Window | undefined,
    result: ReturnType<typeof func> | undefined,
    timerId: NodeJS.Timeout | undefined,
    lastCallTime: number | undefined,
    maxWait: number | undefined,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lastInvokeTime = 0,
    leading = false,
    maxing = false,
    trailing = true;

  if (options) {
    leading = Boolean(options.leading);
    if (options.hasOwnProperty('maxWait')) {
      maxing = true;
      maxWait = Math.max(options.maxWait || 0, wait);
    }
    if (options.hasOwnProperty('trailing')) trailing = Boolean(options.trailing);
  }

  const invokeFunc = (time: number): typeof result => {
    const args = lastArgs as NonNullable<typeof lastArgs>,
      thisArg = lastThis;
    lastArgs = lastThis = undefined;
    lastInvokeTime = time;

    result = func.apply(thisArg, args);
    return result;
  };

  const shouldInvoke = (time: number): boolean => {
    if (lastCallTime === undefined) return true;

    const timeSinceLastCall = time - lastCallTime;
    return (
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      Boolean(maxing && maxWait && time - lastInvokeTime >= maxWait)
    );
  };

  const trailingEdge = (time: number): typeof result => {
    timerId = undefined;

    if (trailing && lastArgs) return invokeFunc(time);

    lastArgs = lastThis = undefined;

    return result;
  };

  const remainingWait = (time: number): number => {
    if (lastCallTime === undefined) return wait;

    const timeSinceLastCall = time - lastCallTime,
      timeSinceLastInvoke = time - lastInvokeTime,
      timeWaiting = wait - timeSinceLastCall;
    return maxing && maxWait ? Math.max(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
  };

  const timerExpired = (): void => {
    const time = Date.now();

    if (shouldInvoke(time)) return trailingEdge(time);

    timerId = setTimeout(timerExpired, remainingWait(time));
  };

  const leadingEdge = (time: number): typeof result => {
    lastInvokeTime = time;

    timerId = setTimeout(timerExpired, wait);

    return leading ? invokeFunc(time) : result;
  };

  const cancel = (): void => {
    if (timerId !== undefined) clearTimeout(timerId);

    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  };

  const flush = (): typeof result => (timerId === undefined ? result : trailingEdge(Date.now()));

  const debounced = (...args: any[]): any => {
    const time = Date.now();
    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (shouldInvoke(time)) {
      if (timerId === undefined) return leadingEdge(lastCallTime);

      if (maxing) {
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }

    if (timerId === undefined) timerId = setTimeout(timerExpired, wait);

    return result;
  };

  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
};

export const throttle = (
  func: (...args: any[]) => any,
  wait: number,
  options?: DebounceOptions
): DebouncedFunc<typeof func> => {
  let leading = true,
    trailing = true;

  if (options) {
    if (options.hasOwnProperty('leading')) leading = Boolean(options.leading);
    if (options.hasOwnProperty('trailing')) trailing = Boolean(options.trailing);
  }

  return debounce(func, wait, { leading, maxWait: wait, trailing });
};

export const inRange = (num: number, start: number, end = 0): boolean =>
  Math.min(start, end) <= num && num < Math.max(start, end);

export const sample = (arr: any[]): typeof arr => {
  if (arr.length < 2) return arr;

  return arr[Math.floor(Math.random() * arr.length)];
};

export const flowRight = (...funcs: Function[]): Function => (initialValue: any) =>
  funcs.reduceRight((acc, fn) => fn(acc), initialValue);
